import React, { useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Clock, User, Tag } from 'lucide-react';

function KanbanView({ items, onItemClick, onItemUpdate, metadata }) {
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    // Get status metadata or use defaults
    if (metadata && metadata.length > 0) {
      setStatuses(metadata);
    } else {
      setStatuses([
        { value: 'planned', label: 'Planned', color: '#6B7280' },
        { value: 'in-progress', label: 'In Progress', color: '#3B82F6' },
        { value: 'done', label: 'Done', color: '#10B981' },
        { value: 'on-hold', label: 'On Hold', color: '#F59E0B' }
      ]);
    }
  }, [metadata]);

  const getItemsByStatus = (status) => {
    return items.filter((item) => item.status === status);
  };

  const handleDrop = async (item, newStatus) => {
    if (item.status !== newStatus) {
      try {
        await onItemUpdate({ ...item, status: newStatus });
      } catch (error) {
        console.error('Failed to update item:', error);
      }
    }
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-view">
      {statuses.map((status) => (
        <KanbanColumn
          key={status.value}
          status={status}
          items={getItemsByStatus(status.value)}
          onItemClick={onItemClick}
          onDrop={handleDrop}
        />
      ))}
    </div>
  );
}

function KanbanColumn({ status, items, onItemClick, onDrop }) {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'kanban-item',
      drop: (item) => {
        onDrop(item, status.value);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [status.value, onDrop]
  );

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-80 bg-card border-2 rounded-xl transition-all ${
        isOver ? 'border-primary bg-accent/5 shadow-lg' : 'border-border'
      }`}
      data-testid={`kanban-column-${status.value}`}
    >
      {/* Column Header */}
      <div 
        className="p-4 border-b-2 border-border"
        style={{ borderBottomColor: status.color + '30' }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: status.color }}
            />
            <h3 className="font-bold text-lg">{status.label}</h3>
          </div>
          <span 
            className="text-sm font-semibold px-2 py-1 rounded-md"
            style={{ 
              backgroundColor: status.color + '20',
              color: status.color
            }}
          >
            {items.length}
          </span>
        </div>
      </div>

      {/* Column Content */}
      <div className="p-3 space-y-3 min-h-[600px] max-h-[calc(100vh-300px)] overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">No items</p>
            <p className="text-xs mt-1">Drag items here</p>
          </div>
        ) : (
          items.map((item) => (
            <KanbanCard
              key={item._id}
              item={item}
              onClick={() => onItemClick(item)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function KanbanCard({ item, onClick }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'kanban-item',
      item: item,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item]
  );

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`bg-background border border-border rounded-lg p-3 cursor-move hover:shadow-lg hover:border-primary transition-all ${
        isDragging ? 'opacity-30' : 'opacity-100'
      }`}
      data-testid={`kanban-item-${item._id}`}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-2">
        <span
          className={`text-xs font-semibold px-2 py-1 rounded uppercase ${
            item.type === 'initiative'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : item.type === 'epic'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
          }`}
        >
          {item.type}
        </span>
        {item.priority && (
          <span
            className={`text-xs px-2 py-1 rounded font-medium ${
              item.priority === 'critical'
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                : item.priority === 'high'
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                : item.priority === 'medium'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {item.priority}
          </span>
        )}
      </div>

      {/* Card Title */}
      <h4 className="font-semibold mb-2 text-sm line-clamp-2">{item.title}</h4>

      {/* Card Description */}
      {item.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
          {item.description}
        </p>
      )}

      {/* Card Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {item.owner && (
            <div className="flex items-center gap-1" title={item.owner}>
              <User className="h-3 w-3" />
              <span className="truncate max-w-[100px]">{item.owner.split('@')[0]}</span>
            </div>
          )}
          {item.timeframe && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span className="uppercase">{item.timeframe}</span>
            </div>
          )}
        </div>
        {item.tags && item.tags.length > 0 && (
          <div className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            <span>{item.tags.length}</span>
          </div>
        )}
      </div>

      {/* Scores */}
      {(item.rice_score || item.wsjf_score || item.ice_score) && (
        <div className="flex gap-1 mt-2">
          {item.rice_score && (
            <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">
              RICE: {item.rice_score.toFixed(1)}
            </span>
          )}
          {item.wsjf_score && (
            <span className="text-xs font-mono bg-secondary/10 text-secondary-foreground px-2 py-0.5 rounded">
              WSJF: {item.wsjf_score.toFixed(1)}
            </span>
          )}
          {item.ice_score && (
            <span className="text-xs font-mono bg-accent/10 text-accent-foreground px-2 py-0.5 rounded">
              ICE: {item.ice_score.toFixed(1)}
            </span>
          )}
        </div>
      )}

      {/* Progress Bar */}
      {item.progress > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{item.progress}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default KanbanView;
